// Wizard için ortak yardımcı fonksiyonlar ve sabitler

// Belge geçerlilik tarihlerini talep aralığında kontrol eden fonksiyon
export const checkDocumentValidity = (documents, endDate, docKeys = ['isg_egitimi_validity', 'saglik_raporu_validity', 'mesleki_yeterlilik_date']) => {
  if (!endDate || !documents) return { valid: true, expiredDocs: [] };
  
  const requestEndDate = String(endDate).slice(0, 10);
  
  const expiredDocs = [];
  
  docKeys.forEach(key => {
    const validityDate = documents?.[key];
    if (validityDate) {
      const docExpiry = String(validityDate).slice(0, 10);
      if (docExpiry < requestEndDate) {
        let docName = key;
        if (key === 'isg_egitimi_validity') docName = 'İSG Eğitimi';
        else if (key === 'saglik_raporu_validity') docName = 'Sağlık Raporu';
        else if (key === 'mesleki_yeterlilik_date') docName = 'Mesleki Yeterlilik';
        else if (key === 'periyodik_muayene_date') docName = 'Periyodik Muayene';
        else if (key === 'operatorluk_belgesi_date') docName = 'Operatörlük Belgesi';
        else if (key === 'mali_mesuliyet_sigortasi_date') docName = 'Mali Mesuliyet Sigortası';
        
        expiredDocs.push({ key, name: docName, expiryDate: validityDate });
      }
    }
  });
  
  return { valid: expiredDocs.length === 0, expiredDocs };
};

// Belge durum badge'i için ortak fonksiyon
export const getDocStatusBadgeClass = (status) => {
  switch (status) {
    case 'onay': return 'bg-emerald-100 text-emerald-700';
    case 'eksik': return 'bg-amber-100 text-amber-700';
    case 'beklemede': return 'bg-slate-100 text-slate-600';
    case 'kapsam_disi': return 'bg-gray-100 text-gray-500';
    case 'suresi_doldu': return 'bg-rose-100 text-rose-700';
    default: return 'bg-slate-100 text-slate-600';
  }
};

// Belge durumu label
export const getDocStatusLabel = (status) => {
  switch (status) {
    case 'onay': return 'Onay';
    case 'eksik': return 'Eksik';
    case 'beklemede': return 'Beklemede';
    case 'kapsam_disi': return 'K.Dışı';
    case 'suresi_doldu': return 'S.Doldu';
    default: return status;
  }
};

// Ekipman türüne göre belgeler
export const getEquipmentDocs = (equipmentType) => {
  // A grubu (araç/iş makinesi): periyodik muayene + mali mesuliyet; B grubu: periyodik muayene + opsiyonel bakım belgesi
  if (equipmentType === 'arac') {
    return [
      { key: 'periyodik_muayene', label: 'Periyodik Muayene', hasDate: true, dateLabel: 'Geçerlilik Tarihi' },
      { key: 'mali_mesuliyet_sigortasi', label: 'Mali Mesuliyet Sigortası', hasDate: true, dateLabel: 'Geçerlilik Tarihi' },
    ];
  }
  return [
    { key: 'periyodik_muayene', label: 'Periyodik Muayene', hasDate: true, dateLabel: 'Geçerlilik Tarihi' },
    { key: 'bakim_belgesi', label: 'Bakım Belgesi (Opsiyonel)', hasDate: false, optional: true },
  ];
};

// Ekipman genel durumunu hesapla
export const getEquipmentStatus = (eq) => {
  if (!eq) return { status: 'beklemede', label: 'Beklemede', color: 'bg-slate-100 text-slate-700' };
  
  const docs = eq.documents || {};
  const equipmentDocs = getEquipmentDocs(eq.equipment_type);
  
  let hasEksik = false;
  let hasBeklemede = false;
  let allOnayOrKapsamDisi = true;
  
  for (const doc of equipmentDocs) {
    const status = docs[doc.key] || 'beklemede';
    if (status === 'kapsam_disi') continue;
    if (doc.optional && status === 'beklemede' && !eq.document_files?.[doc.key]) continue;
    
    if (status === 'eksik') {
      hasEksik = true;
      allOnayOrKapsamDisi = false;
    } else if (status === 'beklemede') {
      hasBeklemede = true;
      allOnayOrKapsamDisi = false;
    } else if (status !== 'onay') {
      allOnayOrKapsamDisi = false;
    }
  }
  
  if (hasEksik) return { status: 'eksik', label: 'Eksik', color: 'bg-amber-100 text-amber-700' };
  if (hasBeklemede) return { status: 'beklemede', label: 'Beklemede', color: 'bg-slate-100 text-slate-700' };
  if (allOnayOrKapsamDisi) return { status: 'onay', label: 'Onaylı', color: 'bg-emerald-100 text-emerald-700' };
  return { status: 'beklemede', label: 'Beklemede', color: 'bg-slate-100 text-slate-700' };
};

// Belge durum seçenekleri
export const DOC_STATUS_OPTIONS = [
  { value: 'beklemede', label: 'Beklemede', color: 'bg-slate-100 text-slate-700' },
  { value: 'onay', label: 'Onay', color: 'bg-emerald-100 text-emerald-700' },
  { value: 'eksik', label: 'Eksik', color: 'bg-amber-100 text-amber-700' },
  { value: 'kapsam_disi', label: 'Kapsam Dışı', color: 'bg-gray-100 text-gray-500' },
];

// Talepte değerlendirilecek personel belgeleri: zorunlular + yüklenmiş/incelenmiş veya seçilmiş opsiyoneller
export const permitEmployeeDocKeys = (emp, workArea, allKeys, selectedOptional = []) => {
  const required = workArea?.required_employee_docs || [];
  if (!workArea || (!required.length && !(workArea.optional_employee_docs || []).length)) return allKeys;
  const touched = (k) => selectedOptional.includes(k) || emp?.document_files?.[k] || (emp?.documents?.[k] && emp.documents[k] !== 'beklemede');
  return allKeys.filter(k => required.includes(k) || touched(k));
};
